/**
 * AB#2139 — what this tab knows about each notification, so the unread badge
 * changes exactly once per notification.
 *
 * The same change can reach a tab more than once: the REST response to its own
 * read or delete plus the server's echo of that change to the user's SignalR
 * group, or the same event on two connections while a token refresh swaps
 * them. Each method records the change and returns how the unread count must
 * move; repeating a change returns 0 (or `null` for a notification already
 * received). An echo for a notification the tab never showed changes nothing:
 * it was never counted here.
 */
type Known = 'unread' | 'read' | 'removed';

interface NotificationState {
    id: number;
    isRead: boolean;
}

export interface UnreadLedger {
    /** A server snapshot replaces what the tab knew; earlier reads and removals stay known. */
    seed(snapshot: ReadonlyArray<NotificationState>): void;
    /** A notification arrived: +1 or 0, or `null` when it was already received (ignore it). */
    arrive(notification: NotificationState): number | null;
    /** A notification was read, here or elsewhere: -1 the first time an unread one is read, else 0. */
    read(id: number): number;
    /** A notification was deleted: -1 when it was unread, else 0. */
    remove(id: number): number;
    readAll(): void;
    clearAll(): void;
}

export function createUnreadLedger(): UnreadLedger {
    let known = new Map<number, Known>();

    return {
        seed(snapshot) {
            const next = new Map<number, Known>();
            known.forEach((state, id) => {
                if (state !== 'unread') next.set(id, state);
            });
            snapshot.forEach(n => next.set(n.id, n.isRead ? 'read' : 'unread'));
            known = next;
        },
        arrive(notification) {
            if (known.has(notification.id)) return null;
            known.set(notification.id, notification.isRead ? 'read' : 'unread');
            return notification.isRead ? 0 : 1;
        },
        read(id) {
            const state = known.get(id);
            if (state === 'removed') return 0;
            known.set(id, 'read');
            return state === 'unread' ? -1 : 0;
        },
        remove(id) {
            const state = known.get(id);
            known.set(id, 'removed');
            return state === 'unread' ? -1 : 0;
        },
        readAll() {
            known.forEach((state, id) => {
                if (state === 'unread') known.set(id, 'read');
            });
        },
        clearAll() {
            known.forEach((_state, id) => known.set(id, 'removed'));
        },
    };
}
